import { useState } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';

export function ProjectHeader() {
  const {
    projects,
    currentProject,
    loadProject,
    createProject,
    loadProjects,
  } = useProjectStore();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // 프로젝트 목록 로드
  useState(() => {
    loadProjects();
  });

  const handleProjectChange = async (projectId: string) => {
    await loadProject(projectId);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      alert('프로젝트 이름을 입력해주세요.');
      return;
    }

    setIsCreating(true);
    try {
      await createProject(newProjectName, '미정', '작가');
      setNewProjectName('');
      setIsCreateDialogOpen(false);
      await loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('프로젝트 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <div className="absolute top-4 right-4 z-10 flex items-center gap-3">
        {/* 프로젝트 선택 */}
        <Select
          value={currentProject?.projectId}
          onValueChange={handleProjectChange}
        >
          <SelectTrigger className="w-[250px] bg-white shadow-md">
            <SelectValue placeholder="프로젝트 선택" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.projectId} value={project.projectId}>
                {project.projectName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 프로젝트 생성 버튼 */}
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="shadow-md"
          size="icon"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* 프로젝트 생성 다이얼로그 */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>새 프로젝트 생성</DialogTitle>
            <DialogDescription>
              프로젝트 이름을 입력하고 생성 버튼을 누르세요.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">프로젝트 이름</Label>
              <Input
                id="project-name"
                placeholder="예: 판타지 대륙의 영웅"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateProject();
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isCreating}
            >
              취소
            </Button>
            <Button onClick={handleCreateProject} disabled={isCreating}>
              {isCreating ? '생성 중...' : '생성'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
